// Time-of-day scheduling: a screen can have slots ("Breakfast 07:00–11:00,
// Mon–Fri") that replace its normal videos and images while they are active.
//
// The same rules are implemented in three places and must stay identical:
// here (web player + admin), server/signage-api.mjs (validation only), and
// apps/android Schedule.kt (the native player).

/** 0 = Sunday … 6 = Saturday, as in JavaScript's Date#getDay. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface ScheduleSlot {
  id: string;
  name: string;
  days: number[];
  /** "HH:MM", 24-hour, UK time. */
  start: string;
  /** "HH:MM". Earlier than start = runs past midnight; equal to start = all day. */
  end: string;
  videos: string[];
  images: string[];
  /** Web pages / YouTube / TikTok links for this slot (see web.ts). */
  web?: string[];
}

/** Every location is in the UK; using this rather than the device's own zone means a box set to the wrong time zone still switches at the right time. */
export const SCHEDULE_TIME_ZONE = "Europe/London";

export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

/** Day of week and minutes past midnight, in UK time. */
export function ukClock(now: Date = new Date()): { day: Weekday; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SCHEDULE_TIME_ZONE,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const day = WEEKDAY_LABELS.indexOf(get("weekday") as (typeof WEEKDAY_LABELS)[number]) as Weekday;
  return { day: day < 0 ? (now.getDay() as Weekday) : day, minutes: Number(get("hour")) * 60 + Number(get("minute")) };
}

/** Whether a slot is running at this UK day/time. */
export function slotIsActive(slot: ScheduleSlot, day: Weekday, minutes: number): boolean {
  const s = toMinutes(slot.start);
  const e = toMinutes(slot.end);
  const on = (d: number) => slot.days.includes((d + 7) % 7);
  if (s === e) return on(day);
  if (s < e) return on(day) && minutes >= s && minutes < e;
  // Overnight, e.g. 22:00–02:00: the evening part belongs to the day it
  // starts on, the small hours to the day before.
  return (on(day) && minutes >= s) || (on(day - 1) && minutes < e);
}

/** The first matching slot wins; null means the screen plays its normal content. */
export function activeSlot(slots: ScheduleSlot[] | undefined, now: Date = new Date()): ScheduleSlot | null {
  if (!slots?.length) return null;
  const { day, minutes } = ukClock(now);
  return slots.find((slot) => slotIsActive(slot, day, minutes)) ?? null;
}
